/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Box,
	Group,
	Title,
	Radio as NativeRadio,
	Tooltip,
} from '@mantine/core';

// Components
import { Select } from '@components/atoms/Select';
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';

// Styles
import classes from './AddOrganizationForm.module.css';

// Utils
import { cleanObject } from '@utils/functions';

// Icons
import useStore from '@store/index';

// I18n
import { TextInput } from '@components/atoms/TextInput';
import { useAddOrganization } from '@features/resources/organizations/api/create-organization';
import { useTranslation } from 'react-i18next';
import { Divider, TextInputV2 } from '@components/atoms';
import { useMediaQuery } from '@mantine/hooks';
import {
	isNotEmpty,
	matches,
	useForm,
	UseFormReturnType,
} from '@mantine/form';
import {
	TFormValuesContact,
	TFormValuesOrganization,
} from '@features/resources/organizations/types';
import { useAddUser } from '@api/create-user';
import {
	REGEX_EMAIL,
	REGEX_PHONE_NUMBER,
} from '@config/constants/regex';
import { NumberInput } from '@components/atoms/NumberInput';
import { ALL_COUNTRIES } from '@config/constants/all-countries';
import { useState } from 'react';
import { PictureUpload } from '@components/molecules/PictureUpload';
import { useUploadPicture } from '@api/upload-picture';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddOrganizationForm = (props: {
	form: UseFormReturnType<TFormValuesOrganization>;
	onCancel: () => void;
	onBack: () => void;
	open: boolean;
}) => {
	const { organizationsPage, organizationsPageSize } =
		useStore();

	const { t } = useTranslation();

	const [pictureData, setPictureData] =
		useState<null | FormData>(null);

	const [ownerId, setOwnerId] = useState<null | string>(
		null
	);

	const uploadPictureMutation = useUploadPicture({
		mutationConfig: {
			onSuccess(data, variables, context) {
				const { name, countryCode, place } =
					props.form.getValues();
				addOrganization.mutate(
					cleanObject({
						name,
						location: {
							countryCode,
							place,
						},
						owner: ownerId,
						logo: data.id,
					})
				);
			},
		},
	});

	const addOrganization = useAddOrganization({
		page: organizationsPage,
		size: organizationsPageSize,
		mutationConfig: {
			onSuccess(data, variables, context) {
				props.onBack();
			},
		},
	});

	const addUser = useAddUser({
		page: organizationsPage,
		size: organizationsPageSize,
		mutationConfig: {
			onSuccess(data) {
				setOwnerId(data.id);
				if (pictureData) {
					uploadPictureMutation.mutate(pictureData);
				} else {
					const { name, countryCode, place } =
						props.form.getValues();
					addOrganization.mutate(
						cleanObject({
							name,
							location: {
								countryCode,
								place,
							},
							owner: data.id,
						})
					);
				}
			},
		},
	});

	const getForm = () => {
		const { onChange: onChangeSite, ...entityProps } =
			props.form.getInputProps('entity');

		/* ******************************** Media Query ******************************* */
		const isBigScreen = useMediaQuery(
			'(min-width: 120em)'
		);

		// Validators
		const formContact = useForm<TFormValuesContact>({
			initialValues: {
				firstName: '',
				lastName: '',
				email: '',
				language: 'fr',
				phoneNumber: '',
			},
			validate: {
				firstName: isNotEmpty(
					t('global.firstNameRequired')
				),
				lastName: isNotEmpty(
					t('global.lastNameRequired')
				),
				email: matches(
					REGEX_EMAIL,
					'form.error.invalidEmail'
				),
				phoneNumber: matches(
					REGEX_PHONE_NUMBER,
					'form.error.invalidPhoneNumber'
				),
			},
		});

		const handleOnUploadProfile = (
			formData: FormData | string | null
		) => {
			if (typeof formData !== 'string') {
				props.form.setFieldValue('logo', formData);
				setPictureData(formData);
			} else {
				props.form.setFieldValue('logo', null);
			}
		};
		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit(() => {})}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{t('global.organizations')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('organizations.addOrganization')}
					</Text>
				</Box>

				{/* Row for Picture */}
				<PictureUpload
					onUpload={handleOnUploadProfile}
					src={''}
				/>

				{/* Row for Name */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInput
							withAsterisk
							visibleFrom="xxl"
							size="md"
							label={t('global.organizationName')}
							placeholder={t('global.organizationName')}
							{...props.form.getInputProps('name')}
						/>
						<TextInput
							withAsterisk
							hiddenFrom="xxl"
							size="sm"
							label={t('global.organizationName')}
							placeholder={t('global.organizationName')}
							{...props.form.getInputProps('name')}
						/>
					</Box>
				</Box>
				<Divider size="xs" />

				{/* Row for Country & Place */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.country')}
							data={ALL_COUNTRIES}
							placeholder={t('global.country')}
							{...props.form.getInputProps('countryCode')}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<TextInputV2
							withAsterisk
							disabled={!props.form.values.countryCode}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.place')}
							placeholder={t('global.place')}
							{...props.form.getInputProps('place')}
						/>
					</Box>
				</Box>
				<Box className={classes.row}>
					<Tooltip label={t('global.comingSoon')}>
						<Box className={classes.containerInput}>
							<NumberInput
								disabled
								hideControls
								maxLength={9}
								withAsterisk
								visibleFrom="xxl"
								size="md"
								label={t('global.legalIdentifier')}
								placeholder={t('global.legalIdentifier')}
								{...props.form.getInputProps(
									'legalIdentifier'
								)}
							/>
							<NumberInput
								disabled
								hideControls
								maxLength={9}
								withAsterisk
								hiddenFrom="xxl"
								size="sm"
								label={t('global.legalIdentifier')}
								placeholder={t('global.legalIdentifier')}
								{...props.form.getInputProps(
									'legalIdentifier'
								)}
							/>
						</Box>
					</Tooltip>
				</Box>
				<Divider size="xs" />
				<Box className={classes.row}>
					<Text className={classes.titleAddContact}>
						{t('global.addOwner')}
					</Text>
				</Box>

				<>
					<Box className={classes.row}>
						<Box className={classes.containerInput}>
							<TextInputV2
								withAsterisk
								size={isBigScreen ? 'md' : 'sm'}
								label={t('global.firstName')}
								placeholder={t('global.firstName')}
								{...formContact.getInputProps(
									'firstName'
								)}
							/>
						</Box>
						<Box className={classes.containerInput}>
							<TextInputV2
								withAsterisk
								size={isBigScreen ? 'md' : 'sm'}
								label={t('global.lastName')}
								placeholder={t('global.lastName')}
								{...formContact.getInputProps('lastName')}
							/>
						</Box>
					</Box>
					<Box className={classes.row}>
						<Box className={classes.containerInput}>
							<TextInputV2
								withAsterisk
								size={isBigScreen ? 'md' : 'sm'}
								label={t('global.email')}
								placeholder={t('global.email')}
								{...formContact.getInputProps('email')}
							/>
						</Box>
					</Box>
					<Box className={classes.row}>
						<Box
							style={{
								width: '30%',
							}}
						>
							<NativeRadio.Group
								withAsterisk
								label={t('global.language')}
								{...formContact.getInputProps('language')}
							>
								<Group gap={'25px'} pt={'md'}>
									<NativeRadio
										variant="outline"
										size={isBigScreen ? 'sm' : 'xs'}
										label={'Fr'}
										value={'fr'}
									/>
									<NativeRadio
										variant="outline"
										size={isBigScreen ? 'sm' : 'xs'}
										label={'En'}
										value={'en'}
									/>
								</Group>
							</NativeRadio.Group>
						</Box>
						<Box className={classes.containerInput}>
							<TextInputV2
								size={isBigScreen ? 'md' : 'sm'}
								label={t('global.phoneNumber')}
								placeholder={t('global.phoneNumber')}
								{...formContact.getInputProps(
									'phoneNumber'
								)}
							/>
						</Box>
					</Box>
				</>
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							hiddenFrom="xxl"
							size="md"
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
						<Button
							fullWidth
							size="lg"
							variant="transparent"
							visibleFrom="xxl"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={() => {
								props.form.validate();
								formContact.validate();

								if (
									formContact.isValid() &&
									props.form.isValid()
								) {
									const values = formContact.getValues();
									const { phoneNumber, ...restValues } =
										values;
									addUser.mutate({
										...restValues,
										...(phoneNumber !== ''
											? {
													phoneNumber,
												}
											: {}),
									});
								}
							}}
							loading={addOrganization.isLoading}
						>
							{t('organizations.addOrganization')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
